/**
 * ParmValSyncPage — Reference · ParmVal Sync.
 *
 * JHA Core Director ParmValSrch → v3 CoreDirectorCodes diff/apply tool. NOT a
 * CRUD table — it's a "rewrite many rows from upstream" sync:
 *
 *   1. Pick a whitelisted JHA ParmName (AcctClsfCode / CustType / AcctRelCode).
 *   2. "Diff vs JHA" fires Customer/ParmValSrch server-side and reshapes the
 *      result against the v3 CoreDirectorCodes catalog → Adds / Updates /
 *      Removes piles.
 *   3. Admin opts in per row (Adds default-on, Update per-field, Removes
 *      default-on), then "Apply" writes the accepted subset. Removes are
 *      deactivations (IsActive=false), never hard DELETEs.
 *
 * Contract (verified against the backend 2026-06-16 — V3AdminParmValSyncFunction
 * + AdminParmValModels; DTOs/routes/shapes all match the ROP-teams editor, no
 * drift):
 *   - GET  /v3/admin/parmval-sync/supported  → { success, data: SupportedParm[] }
 *   - POST /v3/admin/parmval-sync/diff  { parmName } → { success, data: DiffOutcome }
 *   - POST /v3/admin/parmval-sync/apply  ParmValApplyInput → { success, data: ApplyOutcome }
 *
 * Migrated 2026-06-16 from ROP-teams Admin (operational app writes no config).
 * Auth: every route is SuperAdmin-gated on the Function side via
 * AdminAuthService (bearer role-claim). Diff/supported authorize the MSAL
 * bearer directly; apply does too — so we drop ROP-teams' legacy bypass-key
 * prompt and use ADMIN_AUTH_TOKEN_SENTINEL (MSAL bearer; no secret in bundle).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
  buildAdminAuthHeaders,
} from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { SelectField } from '@/components/FormField';

interface SupportedParm {
  parmName: string;
  domain: string;
  displayLabel: string;
  description: string;
}

interface ParmRow {
  code: string;
  label: string;
  description?: string | null;
}

interface FieldDiff {
  fieldName: 'label' | 'description';
  oldValue: string | null;
  newValue: string | null;
}

interface UpdateRow {
  code: string;
  oldLabel: string;
  oldDescription: string | null;
  oldIsActive: boolean;
  newLabel: string;
  newDescription: string | null;
  fields: FieldDiff[];
}

interface RemoveRow {
  code: string;
  label: string;
  isActive: boolean;
}

interface DiffOutcome {
  parmName: string;
  domain: string;
  displayLabel: string;
  adds: ParmRow[];
  updates: UpdateRow[];
  removes: RemoveRow[];
  jhaSourceCount: number;
  v3SourceCount: number;
  fetchTimingMs: number;
  warnings: string[];
  error?: string | null;
  code?: string | null;
}

interface ApplyOutcome {
  success: boolean;
  parmName: string;
  domain: string;
  addedCount: number;
  updatedCount: number;
  deactivatedCount: number;
  elapsedMs: number;
  errors: string[];
}

interface Envelope<T> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
}

/** One flattened Update field-row (a single label/description change). */
interface UpdateFieldRow {
  code: string;
  fieldName: 'label' | 'description';
  oldValue: string | null;
  newValue: string | null;
}

export function ParmValSyncPage(): JSX.Element {
  const client = useApiClient();

  const [supported, setSupported] = useState<ReadonlyArray<SupportedParm> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [parm, setParm] = useState('');

  const [diff, setDiff] = useState<DiffOutcome | null>(null);
  const [diffing, setDiffing] = useState(false);
  const [diffError, setDiffError] = useState<string | null>(null);

  // Accept state — keyed by Code.
  const [adds, setAdds] = useState<Record<string, boolean>>({});
  const [updFields, setUpdFields] = useState<Record<string, Record<string, boolean>>>({});
  const [removes, setRemoves] = useState<Record<string, boolean>>({});

  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);

  // ── Load the server-side whitelist (drives the dropdown). ──────────────
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setSupported(null);
      setLoadError(null);
      try {
        const resp = await client.http.get<Envelope<ReadonlyArray<SupportedParm>>>(
          '/v3/admin/parmval-sync/supported',
        );
        if (cancelled) return;
        if (resp.success && resp.data) setSupported(resp.data);
        else setLoadError(resp.error ?? 'Failed to load supported ParmNames.');
      } catch (e) {
        if (cancelled) return;
        setLoadError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client]);

  const parmOptions = useMemo(
    () =>
      (supported ?? []).map((p) => ({
        value: p.parmName,
        label: `${p.parmName} · ${p.displayLabel}`,
      })),
    [supported],
  );

  const activeParm = useMemo(
    () => (supported ?? []).find((p) => p.parmName === parm) ?? null,
    [supported, parm],
  );

  // ── Diff vs JHA. ───────────────────────────────────────────────────────
  const runDiff = useCallback(async () => {
    if (!parm) {
      setDiffError('Pick a ParmName first.');
      return;
    }
    setDiffing(true);
    setDiffError(null);
    setApplyError(null);
    setDiff(null);
    setAdds({});
    setUpdFields({});
    setRemoves({});
    try {
      const resp = await client.http.post<Envelope<DiffOutcome>>('/v3/admin/parmval-sync/diff', {
        body: { parmName: parm },
      });
      if (resp.success && resp.data) {
        const d = resp.data;
        setDiff(d);
        // Defaults: all Adds checked, all Removes checked, all Update fields checked.
        setAdds(Object.fromEntries(d.adds.map((x) => [x.code, true])));
        setUpdFields(
          Object.fromEntries(
            d.updates.map((x) => [x.code, Object.fromEntries(x.fields.map((f) => [f.fieldName, true]))]),
          ),
        );
        setRemoves(Object.fromEntries(d.removes.map((x) => [x.code, true])));
      } else {
        setDiffError(resp.error ?? 'Diff failed.');
      }
    } catch (e) {
      setDiffError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setDiffing(false);
    }
  }, [client, parm]);

  // ── Accepted counts. ───────────────────────────────────────────────────
  const acceptedAddsCount = diff?.adds.filter((x) => adds[x.code]).length ?? 0;
  const acceptedUpdatesCount =
    diff?.updates.filter((x) => Object.values(updFields[x.code] ?? {}).some(Boolean)).length ?? 0;
  const acceptedRemovesCount = diff?.removes.filter((x) => removes[x.code]).length ?? 0;
  const totalAccepted = acceptedAddsCount + acceptedUpdatesCount + acceptedRemovesCount;

  // ── Apply the accepted subset. ─────────────────────────────────────────
  const apply = useCallback(async () => {
    if (!diff) return;
    setApplying(true);
    setApplyError(null);
    try {
      const body = {
        parmName: diff.parmName,
        acceptedAdds: diff.adds.filter((x) => adds[x.code]),
        acceptedUpdates: diff.updates
          .filter((x) => Object.values(updFields[x.code] ?? {}).some(Boolean))
          .map((x) => ({
            code: x.code,
            fieldsToApply: Object.entries(updFields[x.code] ?? {})
              .filter(([, v]) => v)
              .map(([k]) => k),
            jha: { code: x.code, label: x.newLabel, description: x.newDescription },
          })),
        acceptedRemoves: diff.removes.filter((x) => removes[x.code]).map((x) => x.code),
      };
      const resp = await client.http.post<Envelope<ApplyOutcome>>('/v3/admin/parmval-sync/apply', {
        headers: buildAdminAuthHeaders(undefined, ADMIN_AUTH_TOKEN_SENTINEL),
        body,
      });
      if (resp.success && resp.data) {
        const d = resp.data;
        setToast({
          tone: 'info',
          text: `Applied: +${d.addedCount} added · ${d.updatedCount} updated · ${d.deactivatedCount} deactivated in ${d.elapsedMs}ms.`,
        });
        await runDiff(); // refresh — should now be clean
      } else {
        setApplyError(resp.error ?? 'Apply failed.');
      }
    } catch (e) {
      setApplyError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setApplying(false);
    }
  }, [client, diff, adds, updFields, removes, runDiff]);

  // ── Flatten Updates into one row per changed field (per-field checkbox). ─
  const updateFieldRows = useMemo<ReadonlyArray<UpdateFieldRow>>(
    () =>
      (diff?.updates ?? []).flatMap((r) =>
        r.fields.map((f) => ({
          code: r.code,
          fieldName: f.fieldName,
          oldValue: f.oldValue,
          newValue: f.newValue,
        })),
      ),
    [diff],
  );

  // ── Column defs. ───────────────────────────────────────────────────────
  const allAddsChecked = !!diff && diff.adds.length > 0 && diff.adds.every((x) => adds[x.code]);
  const allRemovesChecked =
    !!diff && diff.removes.length > 0 && diff.removes.every((x) => removes[x.code]);

  const addsColumns: ReadonlyArray<DataTableColumn<ParmRow>> = useMemo(
    () => [
      {
        header: '✓',
        width: 36,
        cell: (r) => (
          <input
            type="checkbox"
            aria-label={`Accept add ${r.code}`}
            checked={!!adds[r.code]}
            onChange={(e) => setAdds((prev) => ({ ...prev, [r.code]: e.target.checked }))}
          />
        ),
      },
      { header: 'Code', cell: (r) => <code className="cell-mono--bold">{r.code}</code> },
      { header: 'Label', cell: (r) => <span className="cell-strong">{r.label}</span> },
      { header: 'Description', cell: (r) => <span className="cell-desc">{r.description ?? '—'}</span> },
    ],
    [adds],
  );

  const updateColumns: ReadonlyArray<DataTableColumn<UpdateFieldRow>> = useMemo(
    () => [
      {
        header: '✓',
        width: 36,
        cell: (r) => (
          <input
            type="checkbox"
            aria-label={`Apply ${r.fieldName} for ${r.code}`}
            checked={!!updFields[r.code]?.[r.fieldName]}
            onChange={(e) =>
              setUpdFields((prev) => ({
                ...prev,
                [r.code]: { ...(prev[r.code] ?? {}), [r.fieldName]: e.target.checked },
              }))
            }
          />
        ),
      },
      { header: 'Code', cell: (r) => <code className="cell-mono--bold">{r.code}</code> },
      { header: 'Field', cell: (r) => <code className="cell-mono">{r.fieldName}</code> },
      {
        header: 'ROP (current)',
        cell: (r) => <span className="cell-desc">{r.oldValue ?? '—'}</span>,
      },
      {
        header: 'JHA (incoming)',
        cell: (r) => <span className="cell-strong">{r.newValue ?? '—'}</span>,
      },
    ],
    [updFields],
  );

  const removeColumns: ReadonlyArray<DataTableColumn<RemoveRow>> = useMemo(
    () => [
      {
        header: '✓',
        width: 36,
        cell: (r) => (
          <input
            type="checkbox"
            aria-label={`Accept remove ${r.code}`}
            checked={!!removes[r.code]}
            onChange={(e) => setRemoves((prev) => ({ ...prev, [r.code]: e.target.checked }))}
          />
        ),
      },
      { header: 'Code', cell: (r) => <code className="cell-mono--bold">{r.code}</code> },
      { header: 'Label', cell: (r) => <span className="cell-strong">{r.label}</span> },
      {
        header: 'Currently active?',
        cell: (r) => (r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>),
      },
    ],
    [removes],
  );

  // ── Render. ────────────────────────────────────────────────────────────
  if (loadError) {
    return (
      <Banner tone="err">
        Could not load supported ParmNames: {loadError}
      </Banner>
    );
  }
  if (!supported) return <Spinner label="Loading supported ParmNames…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Card
        title="ParmVal Sync — JHA Core Director → CoreDirectorCodes"
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <SelectField
                label=""
                value={parm}
                onChange={(v) => {
                  setParm(v);
                  setDiff(null);
                  setDiffError(null);
                  setApplyError(null);
                }}
                options={parmOptions}
                placeholder="— pick a ParmName —"
              />
            </div>
            <Button variant="primary" onClick={() => void runDiff()} disabled={!parm || diffing}>
              {diffing ? 'Diffing…' : 'Diff vs JHA'}
            </Button>
          </div>
        }
      >
        <p className="cell-desc">
          Pulls canonical JHA Core Director parameter values via Customer/ParmValSrch and reconciles
          them against the ROP CoreDirectorCodes catalog. Review the Adds / Updates / Removes below and
          apply the subset you accept. Removes are deactivations (never hard deletes). SuperAdmin only.
        </p>
        {activeParm && (
          <p className="cell-desc">
            <strong>{activeParm.displayLabel}</strong> → CoreDirectorCodes domain{' '}
            <code className="cell-mono">{activeParm.domain}</code>. {activeParm.description}
          </p>
        )}
        {diffError && <Banner tone="err">{diffError}</Banner>}
        {diffing && <Banner tone="info">Querying JHA Customer/ParmValSrch…</Banner>}
      </Card>

      {diff && (
        <>
          <Card title="Diff summary">
            <div className="card-actions-row">
              <Pill tone="info">
                {diff.parmName} · {diff.domain}
              </Pill>
              <span className="cell-desc num">
                JHA returned {diff.jhaSourceCount} rows · ROP has {diff.v3SourceCount} rows · fetched in{' '}
                {diff.fetchTimingMs}ms
              </span>
              {diff.warnings.map((w, i) => (
                <Pill key={i} tone="warn">
                  {w}
                </Pill>
              ))}
            </div>
          </Card>

          <Card title={`Adds (${diff.adds.length}) — in JHA, not in ROP`}>
            {diff.adds.length > 0 && (
              <p className="cell-desc">
                <label className="form-bool">
                  <input
                    type="checkbox"
                    aria-label="Select all adds"
                    checked={allAddsChecked}
                    onChange={(e) =>
                      setAdds(Object.fromEntries(diff.adds.map((x) => [x.code, e.target.checked])))
                    }
                  />
                  <span>Select all</span>
                </label>
              </p>
            )}
            <DataTable<ParmRow>
              columns={addsColumns}
              rows={diff.adds}
              rowKey={(r) => r.code}
              emptyState={<p className="empty">No new rows from JHA.</p>}
            />
          </Card>

          <Card title={`Updates (${diff.updates.length}) — same code, label/description differs`}>
            <DataTable<UpdateFieldRow>
              columns={updateColumns}
              rows={updateFieldRows}
              rowKey={(r) => `${r.code}-${r.fieldName}`}
              emptyState={<p className="empty">No label/description changes from JHA.</p>}
            />
          </Card>

          <Card title={`Removes (${diff.removes.length}) — in ROP, not in JHA (deactivate, never delete)`}>
            {diff.removes.length > 0 && (
              <p className="cell-desc">
                <label className="form-bool">
                  <input
                    type="checkbox"
                    aria-label="Select all removes"
                    checked={allRemovesChecked}
                    onChange={(e) =>
                      setRemoves(Object.fromEntries(diff.removes.map((x) => [x.code, e.target.checked])))
                    }
                  />
                  <span>Select all</span>
                </label>
              </p>
            )}
            <DataTable<RemoveRow>
              columns={removeColumns}
              rows={diff.removes}
              rowKey={(r) => r.code}
              emptyState={<p className="empty">No stale rows in ROP.</p>}
            />
          </Card>

          <Card title="Apply">
            {applyError && <Banner tone="err">{applyError}</Banner>}
            <div className="card-actions-row">
              <span className="cell-desc num">
                Will apply: +{acceptedAddsCount} add{acceptedAddsCount === 1 ? '' : 's'} ·{' '}
                {acceptedUpdatesCount} update{acceptedUpdatesCount === 1 ? '' : 's'} ·{' '}
                {acceptedRemovesCount} deactivation{acceptedRemovesCount === 1 ? '' : 's'}
              </span>
              <Button
                variant="primary"
                onClick={() => void apply()}
                disabled={totalAccepted === 0 || applying}
              >
                {applying ? 'Applying…' : `Apply (${totalAccepted} change${totalAccepted === 1 ? '' : 's'})`}
              </Button>
            </div>
          </Card>
        </>
      )}
    </>
  );
}
